/**
 * 激活转化看板（ADM-MTR-S013）共享契约：GET /api/v1/metrics/activation-conversion 的查询参数与响应形态。
 *
 * 以激活为 cohort（dev-docs AD §15）：
 * - 激活 = `user_attributions` 首次归因绑定（含 organic），时刻 COALESCE(install_time, bound_at)，
 *   按 App × canonical 用户取全部关联身份中最早的一条
 * - 各级只看 [激活时刻, 观察截止) 的行为：进广告小说 → 进付费章 → 进付费面板 → 创建付费订单 → 付费
 * - 每个激活用户一行、维度全部用户级，任意维度组合直接计数
 */

import { z } from 'zod';
import {
  PAYWALL_CONVERSION_MAX_DAYS,
  metricsCalendarDay,
  metricsRepeatedListParam,
} from './metrics-paywall-conversion';

/** 分组维度闭集（书籍名称维度 `book` 以投放落点 novel_id 为键，名称见 options.books） */
export const ACTIVATION_CONVERSION_DIMENSIONS = [
  'date',
  'hour',
  'app',
  'platform',
  'app_version',
  'channel',
  'country',
  'ad_language',
  'book_code',
  'book',
] as const;
export const activationConversionDimensionSchema = z.enum(ACTIVATION_CONVERSION_DIMENSIONS);
export type ActivationConversionDimension = z.infer<typeof activationConversionDimensionSchema>;

/** 渠道类型 = user_attributions.attribution_status 闭集 */
export const ACTIVATION_CHANNEL_TYPES = ['NON_ORGANIC', 'ORGANIC', 'ORGANIC_OR_UNKNOWN'] as const;

/** 单次查询最多跨 92 天（与付费转化看板同源：转化各级依赖保留 90 天的埋点原始行） */
export const ACTIVATION_CONVERSION_MAX_DAYS = PAYWALL_CONVERSION_MAX_DAYS;

/** 分组行返回上限；超出时 `truncated=true` */
export const ACTIVATION_CONVERSION_MAX_ROWS = 5000;

/** 以重复查询参数传递的多值参数名（路由按此把同名参数收成数组） */
export const ACTIVATION_CONVERSION_LIST_PARAMS = [
  'hours',
  'app_ids',
  'channel_types',
  'app_versions',
  'countries',
  'ad_languages',
  'book_codes',
  'novel_ids',
] as const;

const uuidList = metricsRepeatedListParam(z.string().uuid()).transform((ids) =>
  ids.map((id) => id.toLowerCase()),
);

export const activationConversionQuerySchema = z
  .object({
    from: metricsCalendarDay,
    to: metricsCalendarDay,
    group_by: z
      .string()
      .optional()
      .transform((raw) => [
        ...new Set(
          (raw ?? '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
        ),
      ])
      // 缺省 / 空串 / 只有分隔符时按日期分组，不产生「零维度只出汇总」的未定义形态
      .transform((dims) => (dims.length ? dims : ['date']))
      .pipe(
        z.array(activationConversionDimensionSchema).max(ACTIVATION_CONVERSION_DIMENSIONS.length),
      ),
    hours: metricsRepeatedListParam(z.string().regex(/^\d{1,2}$/, '小时须为 0-23 的整数'))
      .transform((values) => [...new Set(values.map(Number))])
      .pipe(z.array(z.number().int().min(0).max(23))),
    app_ids: uuidList,
    channel_types: metricsRepeatedListParam(z.enum(ACTIVATION_CHANNEL_TYPES)),
    app_versions: metricsRepeatedListParam(z.string().max(32)),
    countries: metricsRepeatedListParam(
      z.string().regex(/^[A-Z]{2}$/, '国家须为 ISO 3166-1 两位大写码'),
    ),
    ad_languages: metricsRepeatedListParam(z.string().max(8)),
    book_codes: metricsRepeatedListParam(z.string().max(32)),
    novel_ids: uuidList,
    min_activations: z.coerce.number().int().min(0).max(100_000_000).optional(),
  })
  // 拒绝未声明参数：误拼的筛选静默丢弃会返回看似合理的全量数据
  .strict()
  .superRefine((query, ctx) => {
    const span =
      (Date.parse(`${query.to}T00:00:00Z`) - Date.parse(`${query.from}T00:00:00Z`)) / 86_400_000;
    if (span < 0) ctx.addIssue({ code: 'custom', path: ['to'], message: 'to 不得早于 from' });
    else if (span + 1 > ACTIVATION_CONVERSION_MAX_DAYS)
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `时间窗最多 ${ACTIVATION_CONVERSION_MAX_DAYS} 天`,
      });
  });
export type ActivationConversionQuery = z.infer<typeof activationConversionQuerySchema>;

/** 分组键：只含请求的 group_by 维度；值为 null = 未知 / 无投放落点 */
export type ActivationConversionDimensionValues = Partial<
  Record<ActivationConversionDimension, string | null>
>;

/** 六级人数：逐级嵌套，后一级只计已到过前面各级的激活用户（不约束先后），人数逐级不增 */
export type ActivationConversionMetrics = {
  activations: number;
  ad_novel_users: number;
  paid_chapter_users: number;
  paywall_users: number;
  order_users: number;
  paid_users: number;
};

export type ActivationConversionRow = {
  dims: ActivationConversionDimensionValues;
} & ActivationConversionMetrics;

export type ActivationConversionBookOption = {
  id: string;
  title: string;
  book_code: string;
  locale: string;
};

export type ActivationConversionData = {
  window: { from: string; to: string; timezone: 'UTC' };
  /** 观察截止 = min(to 次日 00:00 UTC, 当前时刻) */
  observed_until: string;
  group_by: ActivationConversionDimension[];
  /** 全窗口汇总（每个激活用户只计一次） */
  summary: ActivationConversionMetrics;
  rows: ActivationConversionRow[];
  truncated: boolean;
  /** 筛选候选值：同 App / 时间窗 / 小时的激活用户中出现过的值（不受其余筛选约束） */
  options: {
    apps: { id: string; name: string; platform: string | null }[];
    app_versions: string[];
    countries: string[];
    ad_languages: string[];
    book_codes: string[];
    books: ActivationConversionBookOption[];
  };
  generated_at: string;
};
